import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';

import {
  JoinOrganizationSearchQuery,
  JoinOrganizationSearchQueryVariables,
} from './__generated/JoinOrganizationSearchQuery';
import {JOIN_ORGANIZATION_LISTING_ORGANIZATION_FRAGMENT} from './JoinOrganizationListing';
import {
  JoinOrganizationSearchBaseProps,
  JoinOrganizationSearchLoading,
  PresentationalJoinOrganizationSearch,
} from './JoinOrganizationSearch';

export const JOIN_ORGANIZATION_SEARCH_QUERY: DocumentNode = gql`
  query JoinOrganizationSearchQuery(
    $searchInput: OrganizationListingSearchInput!
    $limitOffsetInput: LimitOffsetInput!
  ) @skipAudit @engComponent(name: IDENTITY) {
    currentTenantOrganizationListing(searchInput: $searchInput, limitOffsetInput: $limitOffsetInput) {
      organizations {
        ...JoinOrganizationListingOrganization
      }
    }
    currentUser {
      ... on UserNode {
        id
        organizations {
          id
        }
      }
    }
  }
  ${JOIN_ORGANIZATION_LISTING_ORGANIZATION_FRAGMENT}
`;

export default function JoinOrganizationSearchListing(
  props: JoinOrganizationSearchBaseProps & {
    queryVariables: JoinOrganizationSearchQueryVariables;
    onClickRequestJoinOrganization: (organizationId: string) => void;
  }
): JSX.Element | null {
  const queryResult = useBenchlingQuery<JoinOrganizationSearchQuery, JoinOrganizationSearchQueryVariables>(
    JOIN_ORGANIZATION_SEARCH_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: props.queryVariables,
    }
  );

  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load organization search; try reloading the page.');
    return null;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return <JoinOrganizationSearchLoading {...props} />;
  } else {
    const organizations = queryResult.data.currentTenantOrganizationListing.organizations;
    const currentUser = queryResult.data.currentUser;
    assert(
      currentUser?.__typename === 'UserNode' || currentUser == null,
      `Non-user should not access this page.`
    );

    const headerText =
      organizations.length === 0 && (props.query.length !== 0 || props.filters.length !== 0)
        ? 'No organizations found. Try searching for something else or create your own organization.'
        : undefined;
    return (
      <PresentationalJoinOrganizationSearch
        {...props}
        currentUser={currentUser}
        organizations={organizations}
        isLoading={false}
        showInstitutionFilter={props.showInstitutionFilter ?? false}
        headerText={headerText}
      />
    );
  }
}
